"use client";

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { MatchScore, type MatchScoreContent } from "fansunited-sports-ui";
import type {
    FUSportsEvent,
    FUSportsLivescoreScore,
    FUSportsLivescoreStatus,
    SearchEntityResult,
} from "fansunited-data-layer";
import { BettingOdds } from "@/features/odds/components/BettingOdds";
import { useServerBatchMatchOdds } from "@/features/odds/useServerBatchMatchOdds";
import { HorizontalScroll } from "@/components/ui/HorizontalScroll";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { formatUpcomingMatchTime } from "@/utilities/formatting/date";
import { fetchLiveEventSnapshots } from "@/features/live-data/liveEventsClient";
import type { LiveEventSnapshot } from "@/features/live-data/snapshots";
import { useTeamLogoUrl } from "@/components/ui/TeamLogo";
import { useCompetitionCarouselData } from "@/views/home/context/CompetitionCarouselDataContext";

const LIVE_REFRESH_INTERVAL_MS = 30000;

export interface MatchCarouselProps {
    cardStyles?: Partial<MatchCarouselCardStyles>;
    /**
     * When true (default), shows past-12h finished/live matches followed by the
     * next 10 upcoming within 14 days. When false, hides recently-finished
     * matches and only shows the upcoming window.
     */
    showRecent?: boolean;
}

export interface MatchCarouselCardStyles {
    container: string;
    shadow: "none" | "sm" | "md" | "lg" | "xl" | "2xl";
    hoverShadow: "none" | "sm" | "md" | "lg" | "xl" | "2xl";
    borderColor: string;
    borderWidth: "0" | "1" | "2" | "4" | "8";
    borderRadius: "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full";
    hoverable: boolean;
    padding: "none" | "sm" | "md" | "lg" | "xl";
    logoSize: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
    typography: {
        teamName: string;
        score: string;
        metadata: string;
        status: string;
    };
}

type ResolveLogoUrl = (opts: {
    logo?: string | null;
    countryFlag?: string | null;
    countryCode?: string | null;
}) => string;

type TeamOrPlayerEntity = Extract<SearchEntityResult, { entityType: "team" | "athlete" }>;
type CompetitionEntity = Extract<SearchEntityResult, { entityType: "competition" }>;

/** Narrow a hydrated entity to a team/athlete competitor, or return undefined. */
function asCompetitor(entity: SearchEntityResult | undefined): TeamOrPlayerEntity | undefined {
    if (!entity) return undefined;
    return entity.entityType === "team" || entity.entityType === "athlete" ? entity : undefined;
}

/** Narrow a hydrated entity to a competition, or return undefined. */
function asCompetition(entity: SearchEntityResult | undefined): CompetitionEntity | undefined {
    if (!entity) return undefined;
    return entity.entityType === "competition" ? entity : undefined;
}

/** Pull the running score value from the sport-specific score-line array. */
function pickScoreValue(
    score: FUSportsLivescoreScore[] | undefined,
    side: "competitorOne" | "competitorTwo"
): string | null {
    if (!score?.length) return null;
    const preferred = score.find((s) => s.key === "result") ?? score.find((s) => s.key === "full_time") ?? score[0];
    return preferred?.[side] ?? null;
}

function isFinishedStatus(status: FUSportsLivescoreStatus | undefined): boolean {
    return status?.type === "FINISHED";
}

function isLiveStatus(status: FUSportsLivescoreStatus | undefined): boolean {
    return status?.type === "LIVE";
}

function mapEventToUI(
    event: FUSportsEvent,
    entities: Record<string, SearchEntityResult>,
    liveSnapshot: LiveEventSnapshot | undefined,
    resolveLogoUrl: ResolveLogoUrl | undefined,
    mounted: boolean
): MatchScoreContent {
    const effectiveStatus = liveSnapshot?.status ?? event.status;
    const effectiveScore = liveSnapshot?.score ?? event.score;
    const effectiveMinute = liveSnapshot?.timing?.currentMinute ?? event.timing?.currentMinute;
    const kickoff = event.startTime ? new Date(event.startTime) : null;
    const isFinished = isFinishedStatus(effectiveStatus);
    const isLive = isLiveStatus(effectiveStatus);

    const c1 = asCompetitor(entities[event.competitorOneId]);
    const c2 = asCompetitor(entities[event.competitorTwoId]);
    const competition = asCompetition(entities[event.competitionId]);

    const c1Name = c1?.name ?? event.competitorOneId;
    const c2Name = c2?.name ?? event.competitorTwoId;

    const c1LogoUrl = resolveLogoUrl
        ? resolveLogoUrl({
              logo: c1?.assets?.logo,
              countryCode: c1?.country?.code,
              countryFlag: c1?.country?.flag,
          })
        : (c1?.assets?.logo ?? "");
    const c2LogoUrl = resolveLogoUrl
        ? resolveLogoUrl({
              logo: c2?.assets?.logo,
              countryCode: c2?.country?.code,
              countryFlag: c2?.country?.flag,
          })
        : (c2?.assets?.logo ?? "");

    const scoreOne = pickScoreValue(effectiveScore, "competitorOne");
    const scoreTwo = pickScoreValue(effectiveScore, "competitorTwo");

    return {
        id: event.id,
        competitorOne: {
            id: event.competitorOneId,
            name: c1Name,
            shortName: c1?.shortName,
            logo: { url: c1LogoUrl, alt: c1Name },
            ...((isFinished || isLive) && scoreOne !== null && { score: { main: scoreOne } }),
        },
        competitorTwo: {
            id: event.competitorTwoId,
            name: c2Name,
            shortName: c2?.shortName,
            logo: { url: c2LogoUrl, alt: c2Name },
            ...((isFinished || isLive) && scoreTwo !== null && { score: { main: scoreTwo } }),
        },
        status: isFinished ? "final" : isLive ? "live" : "scheduled",
        metadata: {
            competition: competition?.name ?? "",
            date: kickoff ? kickoff.toISOString().split("T")[0] : "",
            time: isLive
                ? effectiveMinute !== undefined
                    ? `${effectiveMinute}'`
                    : "LIVE"
                : kickoff && mounted
                  ? formatUpcomingMatchTime(kickoff)
                  : " ",
        },
    };
}

/**
 * Past 12h finished/live + next 10 upcoming within 14d, chronologically sorted.
 * Mirrors the pre-migration shape driven off match.timing.kickoffTime.
 */
function filterCarouselEvents(events: FUSportsEvent[], showRecent: boolean): FUSportsEvent[] {
    const now = new Date();
    const past12h = new Date(now.getTime() - 12 * 60 * 60 * 1000);
    const next14d = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

    const recent: FUSportsEvent[] = [];
    const upcoming: FUSportsEvent[] = [];

    for (const event of events) {
        if (!event.startTime) continue;
        const kickoff = new Date(event.startTime);
        if (showRecent && kickoff >= past12h && kickoff <= now) recent.push(event);
        else if (kickoff > now && kickoff <= next14d) upcoming.push(event);
    }

    upcoming.sort((a, b) => new Date(a.startTime!).getTime() - new Date(b.startTime!).getTime());
    return [...recent, ...upcoming.slice(0, 10)].sort(
        (a, b) => new Date(a.startTime!).getTime() - new Date(b.startTime!).getTime()
    );
}

/** Live-now OR starting within 2 minutes → include in the live refresh set. */
function getEventsNeedingPolling(events: FUSportsEvent[]): string[] {
    const now = new Date();
    const twoMinFromNow = new Date(now.getTime() + 2 * 60 * 1000);
    return events
        .filter((event) => {
            if (isLiveStatus(event.status)) return true;
            if (!event.startTime) return false;
            const kickoff = new Date(event.startTime);
            return kickoff >= now && kickoff <= twoMinFromNow;
        })
        .map((event) => event.id);
}

/**
 * Milliseconds until the earliest not-live, not-finished event enters its
 * 2-minute pre-kickoff window (clamped to [1 s, max timer delay]); null when none lies ahead.
 */
function nextCarouselCheckDelayMs(events: FUSportsEvent[], now: Date): number | null {
    let earliest: number | null = null;
    for (const event of events) {
        if (!event.startTime || isLiveStatus(event.status) || isFinishedStatus(event.status)) continue;
        const windowStart = new Date(event.startTime).getTime() - 2 * 60 * 1000;
        if (windowStart <= now.getTime()) continue;
        if (earliest === null || windowStart < earliest) earliest = windowStart;
    }
    if (earliest === null) return null;
    return Math.min(Math.max(earliest - now.getTime(), 1000), 2 ** 31 - 1);
}

export const MatchCarousel: React.FC<MatchCarouselProps> = ({ cardStyles, showRecent = true }) => {
    const data = useCompetitionCarouselData();
    const nav = useNavigation();
    const router = useRouter();
    const resolveLogoUrl = useTeamLogoUrl();
    const { hasOdds } = useTenantSettings();

    // TZ-dependent time strings must wait for mount to avoid hydration mismatch.
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    const events = data?.events ?? [];
    const entities = data?.entities ?? {};

    const carouselEvents = useMemo(() => filterCarouselEvents(events, showRecent), [events, showRecent]);

    const [liveSnapshots, setLiveSnapshots] = useState<Map<string, LiveEventSnapshot>>(new Map());

    // `tick` re-runs the selection when the next event enters its pre-kickoff window.
    const [tick, setTick] = useState(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pollingIds = useMemo(() => getEventsNeedingPolling(carouselEvents), [carouselEvents, tick]);
    const pollingIdsKey = useMemo(() => [...pollingIds].sort().join(","), [pollingIds]);

    const runRefresh = useCallback(async () => {
        if (pollingIds.length === 0) return;
        const snapshots = await fetchLiveEventSnapshots(pollingIds);
        if (snapshots.length === 0) return;
        setLiveSnapshots((prev) => {
            const next = new Map(prev);
            for (const snap of snapshots) next.set(snap.id, snap);
            return next;
        });
    }, [pollingIds]);

    useEffect(() => {
        if (pollingIds.length === 0) return;
        runRefresh();
        const intervalId = setInterval(runRefresh, LIVE_REFRESH_INTERVAL_MS);
        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollingIdsKey]);

    useEffect(() => {
        if (pollingIds.length > 0) return;
        const delay = nextCarouselCheckDelayMs(carouselEvents, new Date());
        if (delay === null) return;
        const timeoutId = setTimeout(() => setTick((t) => t + 1), delay);
        return () => clearTimeout(timeoutId);
    }, [pollingIds, carouselEvents]);

    const oddsMatchIds = useMemo(
        () =>
            carouselEvents
                .filter((event) => !isFinishedStatus(liveSnapshots.get(event.id)?.status ?? event.status))
                .map((event) => event.id),
        [carouselEvents, liveSnapshots]
    );
    const hasLiveMatches = useMemo(
        () => carouselEvents.some((event) => isLiveStatus(liveSnapshots.get(event.id)?.status ?? event.status)),
        [carouselEvents, liveSnapshots]
    );
    const { oddsMap: batchOddsMap } = useServerBatchMatchOdds({
        matchIds: oddsMatchIds,
        markets: ["1x2"],
        isLive: hasLiveMatches,
    });

    if (carouselEvents.length === 0) return null;

    return (
        <section className="w-full">
            <HorizontalScroll gap="gap-3" arrowSize="md">
                {carouselEvents.map((event) => {
                    const liveSnap = liveSnapshots.get(event.id);
                    const effectiveStatus = liveSnap?.status ?? event.status;
                    const isFinished = isFinishedStatus(effectiveStatus);
                    const isLive = isLiveStatus(effectiveStatus);
                    const matchScoreData = mapEventToUI(event, entities, liveSnap, resolveLogoUrl, mounted);
                    const matchHref = nav.match(event.id);
                    const c1 = asCompetitor(entities[event.competitorOneId]);
                    const c2 = asCompetitor(entities[event.competitorTwoId]);

                    return (
                        <div
                            key={event.id}
                            className="shrink-0 w-72 md:w-88 cursor-pointer match-card-surface overflow-hidden"
                            onClick={(e) => {
                                if ((e.target as HTMLElement).closest("a")) return;
                                router.push(matchHref);
                            }}
                        >
                            <MatchScore
                                match={matchScoreData}
                                variant="scoreCard"
                                odds={
                                    !isFinished && hasOdds ? (
                                        <>
                                            <div className="md:hidden">
                                                <BettingOdds
                                                    matchId={event.id}
                                                    markets={["1x2"]}
                                                    isLive={isLive}
                                                    prefetchedOdds={batchOddsMap}
                                                    styles={{ mode: "flash" }}
                                                    market="FT_1X2"
                                                    kickoffAt={event.startTime ?? undefined}
                                                />
                                            </div>
                                            <div className="hidden md:block">
                                                <BettingOdds
                                                    matchId={event.id}
                                                    markets={["1x2"]}
                                                    isLive={isLive}
                                                    prefetchedOdds={batchOddsMap}
                                                    market="FT_1X2"
                                                    kickoffAt={event.startTime ?? undefined}
                                                />
                                            </div>
                                        </>
                                    ) : undefined
                                }
                                styles={{
                                    container: cardStyles?.container ?? "bg-transparent h-full",
                                    background: "bg-transparent text-card-foreground",
                                    shadow: cardStyles?.shadow ?? "none",
                                    hoverShadow: cardStyles?.hoverShadow ?? "none",
                                    borderColor: cardStyles?.borderColor ?? "border-transparent",
                                    borderRadius: cardStyles?.borderRadius ?? "none",
                                    borderWidth: cardStyles?.borderWidth ?? "0",
                                    hoverable: cardStyles?.hoverable ?? true,
                                    padding: cardStyles?.padding ?? "sm",
                                    logoSize: cardStyles?.logoSize ?? "sm",
                                    typography: {
                                        teamName:
                                            cardStyles?.typography?.teamName ?? "text-sm font-medium !text-current",
                                        score: cardStyles?.typography?.score ?? "text-base font-bold !text-current",
                                        metadata: cardStyles?.typography?.metadata ?? "text-xs !text-current",
                                        status: isLive
                                            ? "text-red-500 font-semibold animate-pulse"
                                            : (cardStyles?.typography?.status ?? "text-xs font-semibold !text-current"),
                                    },
                                }}
                            />
                        </div>
                    );
                })}
            </HorizontalScroll>
        </section>
    );
};
